<template>
  <section class="wt-support">
    <div class="wt-support-inner">
      <welcome-title-advanced
        class="mb-8"
        :title-text="supportTeamTitle"
      />
      <p class="wt-support-intro">
        {{ supportTeamIntro }}
      </p>
      <vue-glide
        ref="slider"
        :type="'carousel'"
        :start-at="1"
        :bound="true"
        :focus-at="'center'"
        :per-view="3"
        :gap="25"
        :breakpoints="{
          992: {
            perView: 1,
          },
        }"
        class="wt-support-carousel"
      >
        <vue-glide-slide
          v-for="(slide, i) in supportTeamSlides"
          :key="i"
        >
          <img
            v-if="slide.image"
            class="wt-support-carousel-image"
            :src="slide.image"
            :alt="slide.alt"
          >
          <img
            v-if="!slide.image"
            class="wt-support-carousel-image"
            src="@/assets/images/placeholder.png"
            alt=""
          >
          <p class="wt-support-carousel-author">
            {{ slide.author }}
          </p>
          <h3 class="wt-support-carousel-title">
            {{ slide.title }}
          </h3>
          <p class="wt-support-carousel-quote">
            {{ slide.content }}
          </p>
          <div class="wt-support-contact-info">
            <div
              v-if="slide.contactInformation.scheduleBookingUrl"
              class="wt-support-contact-info-item"
            >
              <button
                type="button"
                aria-label="Open scheduler"
                class="wt-support-contact-open-booking-scheduler"
                :data-schedule-booking-url="
                  'https://outlook.office365.com/owa/calendar/' +
                    slide.contactInformation.scheduleBookingUrl
                "
              >
                <Calendar class="icon" />
              </button>
            </div>
            <div
              v-if="slide.contactInformation.whatsapp.url"
              class="wt-support-contact-info-item"
            >
              <a
                :href="slide.contactInformation.whatsapp.url"
                class="wt-support-contact-whatsapp"
                aria-label="Whatsapp us"
                :data-whatsapp-intro="slide.contactInformation.whatsapp.modalIntro"
                :data-whatsapp-url="slide.contactInformation.whatsapp.url"
              >
                <WhatsApp class="icon" />
              </a>
            </div>
            <div
              v-if="slide.contactInformation.emailContactPageUrl"
              class="wt-support-contact-info-item"
            >
              <a
                :href="slide.contactInformation.emailContactPageUrl"
                aria-label="Email us"
              >
                <EmailEdit class="icon" />
              </a>
            </div>
            <div
              v-if="slide.contactInformation.phone"
              class="wt-support-contact-info-item wt-support-contact-info-item--phone"
            >
              <a
                :href="slide.contactInformation.phone.phoneNumber"
                class="wt-support-contact-phone-number"
                aria-label="Call us"
                :data-phone-number="slide.contactInformation.phone.phoneNumber"
                :data-phone-modal-intro="slide.contactInformation.phone.modalIntro"
              >
                <Phone class="icon" />
              </a>
            </div>
          </div>
        </vue-glide-slide>
        <template slot="control">
          <button
            data-glide-dir="<"
            class="glide__arrow glide__arrow--left"
            :class="{ 'glide__arrow--disabled': noPrevSlide }"
          >
            <arrow-left-white class="icon" />
          </button>
          <button
            data-glide-dir=">"
            class="glide__arrow glide__arrow--right"
            :class="{ 'glide__arrow--disabled': noNextSlide }"
          >
            <arrow-right-white class="icon" />
          </button>
        </template>
      </vue-glide>
    </div>

    <modal
      v-show="isBookingModalOpen"
      :modal="isBookingModalOpen"
      class="welcome-support-team__book-meeting-modal"
      @close="closeBookingModal"
    >
      <template slot="body">
        <h4 class="modal-content-title">
          {{ i18n.contactInfo.scheduleBookingTitle }}
        </h4>
        <iframe
          :src="scheduleBookingSrc"
          sandbox="allow-same-origin allow-scripts allow-forms"
          title="Booking Scheduler"
          class="modal-content-iframe"
        />
      </template>
    </modal>

    <qr-code-modal
      :title="i18n.contactInfo.phoneNumberModalTitle"
      :intro-html="phoneNumberModal.intro"
      :qr-code-value="phoneNumberModal.phoneNumber"
      :is-open="phoneNumberModal.isOpen"
      @close="closePhoneNumberModal"
    />

    <qr-code-modal
      :title="i18n.contactInfo.whatsappModalTitle"
      :intro-html="whatsappModal.intro"
      :qr-code-value="whatsappModal.whatsappUrl"
      :is-open="whatsappModal.isOpen"
      @close="closeWhatsappModal"
    />
  </section>
</template>

<script>
import Modal from '@/components/modal/Modal.vue';
import QrCodeModal from '@/components/QrCodeModal.vue';
import WelcomeTitleAdvanced from '@/components/welcome/WelcomeTitleAdvanced.vue';
import isMobile from 'is-mobile';
import { Glide, GlideSlide } from 'vue-glide-js';
import 'vue-glide-js/dist/vue-glide.css';

export default {
  name: 'WelcomeSupportTeam',
  components: {
    [Glide.name]: Glide,
    [GlideSlide.name]: GlideSlide,
    Modal,
    QrCodeModal,
    WelcomeTitleAdvanced,
    ArrowLeftWhite: () => import(/* webpackChunkName: "main"*/ '@/components/svg/ArrowLeftWhite.vue'),
    ArrowRightWhite: () => import(/* webpackChunkName: "main"*/ '@/components/svg/ArrowRightWhite.vue'),
    Calendar: () => import(/* webpackChunkName: "main"*/ '@/components/svg/Calendar.vue'),
    WhatsApp: () => import(/* webpackChunkName: "main"*/ '@/components/svg/Whatsapp.vue'),
    Phone: () => import(/* webpackChunkName: "main"*/ '@/components/svg/Phone.vue'),
    EmailEdit: () => import(/* webpackChunkName: "main"*/ '@/components/svg/EmailEdit.vue'),
  },
  props: {
    i18n: {
      type: Object,
      default: null,
    },
    supportTeamTitle: {
      type: String,
      default: '',
    },
    supportTeamIntro: {
      type: String,
      default: '',
    },
    supportTeamSlides: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      noPrevSlide: false,
      noNextSlide: false,
      isBookingModalOpen: false,
      scheduleBookingSrc: '',
      whatsappModal: {},
      phoneNumberModal: {},
    };
  },
  mounted() {
    document
      .querySelectorAll('.wt-support-contact-phone-number')
      .forEach((e) => e.addEventListener('click', this.openPhoneNumberModalIfNotMobile));
    document
      .querySelectorAll('.wt-support-contact-open-booking-scheduler')
      .forEach((e) => e.addEventListener('click', this.openBookingModal));
    document
      .querySelectorAll('.wt-support-contact-whatsapp')
      .forEach((e) => e.addEventListener('click', this.openWhatsappModal));
  },
  beforeDestroy() {
    document
      .querySelectorAll('.wt-support-contact-phone-number')
      .forEach((e) => e.removeEventListener('click', this.openPhoneNumberModalIfNotMobile));
    document
      .querySelectorAll('.wt-support-contact-open-booking-scheduler')
      .forEach((e) => e.removeEventListener('click', this.openBookingModal));
    document
      .querySelectorAll('.wt-support-contact-whatsapp')
      .forEach((e) => e.removeEventListener('click', this.openWhatsappModal));
  },
  methods: {
    cleanMessage() {
      return this.$sanitize(this.titleText);
    },
    carouselArrowStates() {
      if (this.$refs.slider.glide.index === 0) {
        this.noPrevSlide = true;
      } else {
        this.noPrevSlide = false;
      }

      if (this.$refs.slider.glide.index + 1 === this.supportTeamSlides.length) {
        this.noNextSlide = true;
      } else {
        this.noNextSlide = false;
      }
    },
    openBookingModal(clickEvent) {
      this.scheduleBookingSrc = clickEvent.currentTarget.getAttribute('data-schedule-booking-url');
      this.isBookingModalOpen = true;
    },
    closeBookingModal() {
      this.isBookingModalOpen = false;
      this.scheduleBookingSrc = '';
    },
    openWhatsappModal(clickEvent) {
      if (!isMobile()) {
        clickEvent.preventDefault();
        this.whatsappModal = {
          intro: clickEvent.currentTarget.getAttribute('data-whatsapp-intro'),
          whatsappUrl: clickEvent.currentTarget.getAttribute('data-whatsapp-url'),
          isOpen: true,
        };
      }
    },
    closeWhatsappModal() {
      this.whatsappModal = {};
    },
    openPhoneNumberModalIfNotMobile(clickEvent) {
      if (!isMobile()) {
        clickEvent.preventDefault();
        this.phoneNumberModal = {
          intro: clickEvent.currentTarget.getAttribute('data-phone-modal-intro'),
          phoneNumber: clickEvent.currentTarget.getAttribute('data-phone-number'),
          isOpen: true,
        };
      }
    },
    closePhoneNumberModal() {
      this.phoneNumberModal = {};
    },
  },
};
</script>

<style lang="postcss">
.wt-support {
  @apply w-full pt-14 pb-16 relative;

  &-inner {
    @apply container mx-auto relative;
  }

  &-intro {
    @apply text-center mx-auto mb-8 whitespace-pre-line;
    max-width: 90%;

    @screen md {
      max-width: 550px;
    }
  }

  &-carousel {
    .wt-support-carousel {
      &-image {
        @apply mb-9;
        height: 270px;
      }

      &-title {
        @apply text-center mb-4 text-3xl;
      }

      &-quote {
        quotes: '“' '”' '‘' '’';
        @apply text-xl text-center mb-4 whitespace-pre-line;

        &::before {
          content: open-quote;
          font-family: initial;
        }
        &::after {
          content: close-quote;
          font-family: initial;
        }
      }

      &-author {
        @apply text-base text-center;
      }
    }

    .glide__bullets {
      position: absolute;
      z-index: 2;
      bottom: 2em;
      left: 50%;
      display: inline-flex;
      list-style: none;
      transform: translateX(-50%);
    }

    .glide__bullet {
      background-color: var(--color-primary);
      width: 9px;
      height: 9px;
      padding: 0;
      border-radius: 50%;
      border: 2px solid transparent;
      transition: all 300ms ease-in-out;
      cursor: pointer;
      line-height: 0;
      box-shadow: 0 0.25em 0.5em 0 rgba(0, 0, 0, 0.1);
      margin: 0 0.25em;

      &:focus {
        outline: none;
      }

      &:hover,
      &:focus {
        border: 2px solid white;
        background-color: rgba(255, 255, 255, 0.5);
      }

      &--active {
        background-color: #b81c22;
      }
    }

    // start: disable carousel styling on desktop
    div[data-glide-el='controls'] {
      @screen md {
        @apply hidden;
      }
    }

    .glide__slides {
      @screen md {
        transform: initial !important;
      }
    }
    // end: disable carousel styling on desktop

    .glide__slide {
      position: relative;

      img {
        width: 100%;
        max-width: 320px;
        height: 320px;
        object-fit: cover;
        mask-image: url('~@/assets/svg/shield-cutout.svg?inline');
        mask-size: cover;
        margin: 0 auto;
        margin-bottom: 44px;

        @screen lg {
          width: 320px;
        }

        @screen xl {
          width: 236px;
          height: 236px;
        }
      }
    }

    .glide__arrow {
      @apply block absolute text-primary-bravo p-2 rounded-xs opacity-100 cursor-pointer;
      z-index: 2;
      box-shadow: 0 0.25em 0.5em 0 rgba(0, 0, 0, 0.1);
      will-change: opacity;
      transition: opacity 150ms ease, border 300ms ease-in-out;
      top: 115px;
      background-color: rgba(255, 255, 255, 0.6);

      .icon {
        width: 18px;
        height: 18px;
      }

      &:focus {
        outline: none;
      }

      &--left {
        left: 5px;
      }

      &--right {
        right: 5px;
      }

      &--disabled {
        opacity: 0.33;
        pointer-events: none;
      }
    }
  }

  &-contact-info {
    @apply flex justify-center gap-3;

    &-item {
      background-image: url('~@/assets/svg/shield-with-shadow.svg?inline');
      background-repeat: no-repeat;
      width: 56px;
      height: 56px;

      > a,
      > button {
        @apply flex flex-col items-center justify-center;

        width: 56px;
        height: 56px;
      }

      .icon {
        @apply text-secondary-alpha;
        width: 20px;
        height: 20px;
      }

      &--phone {
        .icon {
          width: 16px;
          height: 16px;
        }
      }
    }
  }

  .welcome-support-team__book-meeting-modal .modal {
    @apply px-0 py-8 rounded-md;

    &-body {
      @apply p-0;
    }

    &-header {
      @apply static border-0;
    }

    &-content-title {
      @apply text-secondary-alpha text-center mb-4;
    }

    &-content-iframe {
      @apply border-t border-b border-gray-100;
      width: 100%;
      height: 80vh;
      max-height: 600px;
    }
  }
}
</style>
